import { useEffect, useRef, useState } from 'react';
import { htmlPreviewDocument } from './previewDocument';
import { cn } from '../../lib/utils';

const PREVIEW_WIDTH = 640;

/**
 * Faithful preview of an HTML newsletter. Separate from the source editor
 * on purpose: it only takes finished HTML, so a later visual editor can
 * drive the same frame. The frame has no scripts and no same-origin access
 * (see `previewDocument.ts`).
 */
export function HtmlPreview({
  html,
  title = 'HTML preview',
  className,
}: {
  /** Sanitised preview HTML from the server. `null` shows the empty state. */
  html: string | null;
  title?: string;
  className?: string;
}) {
  const empty = html === null || html.trim() === '';
  const box = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState<{ w: number; h: number } | null>(null);

  // Newsletters are about 600 px wide. In a narrow pane the frame is laid
  // out at that width and scaled down, so nothing is cut off at the edge.
  useEffect(() => {
    const el = box.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const measure = () => setSize({ w: el.clientWidth, h: el.clientHeight });
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [empty]);

  if (html === null || empty) {
    return (
      <div
        className={cn(
          'flex items-center justify-center rounded-xl border border-border bg-card p-6 text-sm text-muted',
          className,
        )}
      >
        Nothing to preview yet.
      </div>
    );
  }
  const scale = size && size.w > 0 ? Math.min(1, size.w / PREVIEW_WIDTH) : 1;
  const scaled = size !== null && scale < 1;
  return (
    <div
      ref={box}
      className={cn('overflow-hidden rounded-xl border border-border bg-white', className)}
    >
      <iframe
        title={title}
        srcDoc={htmlPreviewDocument(html)}
        sandbox=""
        tabIndex={-1}
        className="block border-0 bg-white"
        style={
          scaled
            ? {
                width: PREVIEW_WIDTH,
                height: size.h / scale,
                transform: `scale(${scale})`,
                transformOrigin: 'top left',
              }
            : { width: '100%', height: '100%' }
        }
      />
    </div>
  );
}
